/**
 * Точки входа и параметры реферальной ссылки.
 * Справочник соответствует прототипу из DEV-459 и чтению параметров в osago-client.
 */

export type EntryPointKey = "home" | "osago" | "pkasko" | "mortgage";

export interface EntryPointDef {
  key: EntryPointKey;
  label: string;
  hint: string;
  path: string;
  /** companies читает только выдача ОСАГО — на своих формах продуктов параметр не применяется. */
  supportsCompanies: boolean;
}

export const ENTRY_POINTS: EntryPointDef[] = [
  {
    key: "home",
    label: "Главная страница",
    hint: "Лендинг с формой расчёта ОСАГО и описанием сервиса",
    path: "/",
    supportsCompanies: true,
  },
  {
    key: "osago",
    label: "Форма расчёта ОСАГО",
    hint: "Клиент попадает на форму, минуя лендинг",
    path: "/osago",
    supportsCompanies: true,
  },
  {
    key: "pkasko",
    label: "Форма ПКаско",
    hint: "Продукт ПКаско от СК Гелиос — отдельная форма",
    path: "/pkasko",
    supportsCompanies: false,
  },
  {
    key: "mortgage",
    label: "Форма ипотечного страхования",
    hint: "Страхование ипотеки — отдельная форма",
    path: "/mortgage",
    supportsCompanies: false,
  },
];

export const DEFAULT_ENTRY_POINT: EntryPointKey = "home";

export function getEntryPoint(key: EntryPointKey): EntryPointDef {
  return ENTRY_POINTS.find((e) => e.key === key) ?? ENTRY_POINTS[0];
}

export function isEntryPointKey(value: string): value is EntryPointKey {
  return ENTRY_POINTS.some((e) => e.key === value);
}

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

export interface UtmFieldDef {
  key: UtmKey;
  placeholder: string;
}

export const UTM_FIELDS: UtmFieldDef[] = [
  { key: "utm_source", placeholder: "например, vk" },
  { key: "utm_medium", placeholder: "например, cpc" },
  { key: "utm_campaign", placeholder: "например, spring2026" },
  { key: "utm_content", placeholder: "например, banner_top" },
];

export type Utm = Partial<Record<UtmKey, string>>;

/** Зарезервировано под режим виджета — в реферальной ссылке использовать нельзя. */
export const RESERVED_UTM_SOURCE = "iframe";

export interface ParamDoc {
  param: string;
  place: "hash" | "query" | "path";
  purpose: string;
  example: string;
}

export const PARAM_DOCS: ParamDoc[] = [
  {
    param: "affid",
    place: "hash",
    purpose: "Обязательный. Партнёрский ID — по нему продажа закрепляется за вами",
    example: "#affid=1024",
  },
  {
    param: "chan",
    place: "hash",
    purpose: "Метка канала или площадки (субID) — статистика по каналам в кабинете партнёра",
    example: "chan=vk_post_01",
  },
  {
    param: "b",
    place: "hash",
    purpose: "ID баннера или креатива — сравнение эффективности размещений",
    example: "b=banner7",
  },
  {
    param: "/osago",
    place: "path",
    purpose: "Вход сразу на форму расчёта ОСАГО, минуя главную страницу",
    example: "osagotop.ru/osago",
  },
  {
    param: "/pkasko",
    place: "path",
    purpose: "Вход на форму ПКаско (продукт СК Гелиос). Выбор списка СК не применяется",
    example: "osagotop.ru/pkasko",
  },
  {
    param: "/mortgage",
    place: "path",
    purpose: "Вход на форму ипотечного страхования. Выбор списка СК не применяется",
    example: "osagotop.ru/mortgage",
  },
  {
    param: "companies",
    place: "query",
    purpose: "В выдаче ОСАГО участвуют только перечисленные СК — коды через запятую. Без параметра — все",
    example: "companies=alpha,ingos",
  },
  {
    param: "header",
    place: "query",
    purpose: "Значение false скрывает шапку сайта",
    example: "header=false",
  },
  {
    param: "footer",
    place: "query",
    purpose: "Значение false скрывает футер сайта",
    example: "footer=false",
  },
  {
    param: "jivochat",
    place: "query",
    purpose: "Значение false отключает онлайн-чат",
    example: "jivochat=false",
  },
  {
    param: "utm_*",
    place: "query",
    purpose: "Метки для веб-аналитики (Яндекс.Метрика): source, medium, campaign, content",
    example: "utm_medium=cpc",
  },
];
